<template>
    <v-card flat>
      <v-card-title>
        Statement
        <div class="ml-auto">
          <v-btn
            @click="showAddRecurringChargeDialog = true"
            outlined
            color="grey darken-1"
            class="mt-3"
          >
            Add Recurring Charge
          </v-btn>
        </div>
      </v-card-title>
  
      <v-card-text>
        <v-data-table
          :headers="recurringChargeHeaders"
          :items="recurringCharges"
          class="elevation-1"
        >
          <template v-slot:item.actions="{ item }">
            <v-icon 
              small 
              @click="editRecurringCharge(item)" 
              class="mr-2 edit-icon"
            >mdi-pencil</v-icon>
            <v-icon 
              small 
              @click="deleteRecurringCharge(item.id)" 
              class="delete-icon"
            >mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card-text>
  
      <!-- Add/Edit Recurring Charge Dialog -->
      <v-dialog v-model="showAddRecurringChargeDialog" max-width="500">
        <v-card>
          <v-card-title>{{ editMode ? "Edit Recurring Charge" : "Add Recurring Charge" }}</v-card-title>
          <v-card-text>
            <v-form ref="addRecurringChargeForm">
              <v-text-field label="Amount" v-model="recurringChargeForm.amount" type="number" required></v-text-field>
  
              <v-select
                label="Transaction Type"
                v-model="recurringChargeForm.transactionType"
                :items="transactionTypeOptions"
                item-text="text"
                item-value="value"
                required
              ></v-select>
  
              <v-text-field label="Lease Reference" v-model="recurringChargeForm.leaseReference" readonly></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click="submitRecurringCharge">{{ editMode ? "Update" : "Submit" }}</v-btn>
            <v-btn text @click="showAddRecurringChargeDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </template>
  
  <script>
  import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
  import { getFirestore } from "firebase/firestore";
  
  const db = getFirestore();
  
  export default {
    props: {
      leaseReference: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        showAddRecurringChargeDialog: false,
        editMode: false,
        recurringCharges: [],
        recurringChargeForm: {
          id: null,
          amount: "",
          transactionType: "",
          leaseReference: this.leaseReference,
        },
        recurringChargeHeaders: [

          { text: "Transaction Type", value: "transactionType" },
          { text: "Amount", value: "amount" },
          { text: "Actions", value: "actions", sortable: false },
        ],
        transactionTypeOptions: [
         { text: "Admin Fee", value: "admin" },
  { text: "Bank Fees", value: "Bank Fees" },
  { text: "Bookkeeping", value: "Bookkeeping" },
  { text: "Charge", value: "Other Charges: Charge" },
  { text: "Collection Fee", value: "Fee Raised: Collection Fee" },
  { text: "Deposit Charged", value: "Deposit Charged" },
  { text: "Fee Raised", value: "admin" },
  { text: "Insurance", value: "Insurance" },
  { text: "Landscaping", value: "Landscaping" },
  { text: "Lease Fees", value: "Lease Fees" },
  { text: "Legal Expenses", value: "Legal Expenses" },
  { text: "Repair and Maintenance (to Landlord)", value: "Repair and Maintenance (to Landlord)" },
  { text: "Repairs and Maintenance", value: "Vendor Charges: Repairs and Maintenance" },
  { text: "Rent Charged", value: "Rent Charged" },
  { text: "Sewerage Av", value: "Sewerage Av" },
  { text: "Sewerage Charged", value: "Sewerage Charged" },
  { text: "Utilities (to Landlord)", value: "Utilities (to Landlord)" },
  { text: "Refuse", value: "Refuse" },
  { text: "Utilities-sewerage", value: "Utilities-sewerage" },
  { text: "Utilities", value: "Utilities" },
  { text: "Water Av", value: "Water Av" } ,
  { text: "Other Vendor Expenses", value: "Vendor Expenses" },
  { text: "Other Charge", value: "Other Charge" },
        ],
      };
    },
    methods: {
      async fetchRecurringCharges() {
  try {
    const querySnapshot = await getDocs(collection(db, "ReccuringCharges"));
    this.recurringCharges = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter((charge) => charge.leaseReference === this.leaseReference) // Filter by leaseReference
      .map((charge) => ({
        ...charge,
        amount: `R ${parseFloat(charge.amount).toFixed(2)}`, // Format amount
      }));
  } catch (error) {
    console.error("Error fetching recurring charges:", error);
  }
},
  
      async submitRecurringCharge() {
        const { id, amount, transactionType, leaseReference } = this.recurringChargeForm;
  
        if (!amount || !transactionType) {
          alert("Please fill in all required fields.");
          return;
        }
  
        try {
          if (this.editMode && id) {
            const chargeRef = doc(db, "ReccuringCharges", id);
            await updateDoc(chargeRef, { amount, transactionType });
            alert("Recurring charge updated successfully!");
          } else {
            await addDoc(collection(db, "ReccuringCharges"), {
              amount,
              transactionType,
              leaseReference,
            });
            alert("Recurring charge added successfully!");
          }
  
          this.showAddRecurringChargeDialog = false;
          this.editMode = false;
          this.resetForm();
          await this.fetchRecurringCharges();
        } catch (error) {
          console.error("Error submitting recurring charge:", error);
          alert("Failed to process recurring charge. Please try again.");
        }
      },
  
      editRecurringCharge(charge) {
        this.recurringChargeForm = { ...charge };
        this.editMode = true;
        this.showAddRecurringChargeDialog = true;
      },
  
      async deleteRecurringCharge(id) {
        if (confirm("Are you sure you want to delete this recurring charge?")) {
          try {
            await deleteDoc(doc(db, "ReccuringCharges", id));
            alert("Recurring charge deleted!");
            await this.fetchRecurringCharges();
          } catch (error) {
            console.error("Error deleting charge:", error);
            alert("Failed to delete charge. Please try again.");
          }
        }
      },
  
      resetForm() {
        this.recurringChargeForm = {
          id: null,
          amount: "",
          transactionType: "",
          leaseReference: this.leaseReference,
        };
      },
    },
  
    created() {
      this.fetchRecurringCharges();
    },
  };
  </script>
  
  <style>
  .edit-icon {
    color: #4caf50;
    cursor: pointer;
  }
  .delete-icon {
    color: #f44336;
    cursor: pointer;
  }
  </style>
    